import {
  CardHeader,
  CardHeaderProps,
} from "@/components/Dashboard/components/Card/Card.Header";
import { CardMain } from "@/components/Dashboard/components/Card/Card.Main";
import { CardDetailsProps } from "@/components/Dashboard/components/Card/Card.Details";
import {
  CardFooter,
  CardFooterProps,
} from "@/components/Dashboard/components/Card/Card.Footer";
import {
  cn,
  firestoreToDateTime,
  getTextColor,
  gradientFromColor,
} from "@/lib/utils";
import {
  CardSidebar,
  CardSidebarProps,
} from "@/components/Dashboard/components/Card/Card.Sidebar";
import { useCardAnimations } from "@/hooks/useCardAnimations";
import { HexColor, Card as CardModel, Shop, TagsDocument } from "@/types/v2";
import { useFeatures } from "@/hooks/useFeatures";
import { useStore } from "@/stores/useStore";
import { getZoomTier } from "@/lib/cardZoom";
import { CSSProperties, Fragment, useCallback, useMemo, useState } from "react";
import { DEFAULT_CARD_COLOR } from "@/lib/const";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { DraggableAttributes } from "@dnd-kit/core";
import "./Card.css";
import { CardRow } from "@/components/Dashboard/components/Card/Card.Row";
import { CardTags } from "@/components/Dashboard/components/Card/Card.Tags";
import { DueInOutTime } from "@/components/Dashboard/components/Card/DueInOutTime";
import { getCardContents } from "@/lib/cards";
import { useRoPanelParams } from "@/hooks/useRoPanelParams";
import { useRoPanelAnalytics } from "@/hooks/useRoPanelAnalytics";
import { isEventFromPortal } from "@/lib/domEvents";

export interface CardProps
  extends Omit<CardSidebarProps, "cardData" | "shop">,
    Omit<
      CardHeaderProps,
      "dndListeners" | "dndAttributes" | "cardData" | "subdomain"
    >,
    Omit<CardDetailsProps, "dndListeners" | "dndAttributes" | "cardData">,
    Omit<
      CardFooterProps,
      | "color"
      | "bgColor"
      | "isCardColorEnabled"
      | "isCardNotesEnabled"
      | "isCardAttachmentsEnabled"
      | "isCardLaborEnabled"
      | "isTasksTabEnabled"
      | "shopId"
      | "cardData"
    > {
  shop: Shop;
  cardData?: CardModel;
  tags?: TagsDocument;
  isDragging?: boolean;
  tentative?: boolean;
  dragHandleListeners?: SyntheticListenerMap;
  dragHandleAttributes?: DraggableAttributes;
  onMoveToClick?: (card: CardModel) => void;
}
export const Card = ({
  cardData,
  employees,
  shop,
  tags,
  isDragging,
  tentative,
  dragHandleListeners,
  dragHandleAttributes,
  integration,
  disabled,
  onMoveToClick,
  ...props
}: CardProps) => {
  const { features } = useFeatures(shop);
  const cardContents = getCardContents(shop);

  const promiseTimePastDueColor = useMemo(
    () => shop.shopConfig?.promiseTime?.pastDueColor,
    [shop]
  );

  const { animationName, getBackgroundColor, automationAction } =
    useCardAnimations({
      cardData: cardData,
      isCardColorSyncEnabled: features.isCardColorSyncEnabled,
      isCardColorEnabled: features.isCardColorEnabled,
      isPromiseTimeEnabled: features.isPromiseTimeEnabled,
      isDragging: isDragging ?? false,
      startTime:
        firestoreToDateTime(cardData?.timer?.timeStarted)?.getTime() ?? null,
      promiseTimePastDueColor: promiseTimePastDueColor,
    });

  const zoom = useStore((s) => s.dashboardCardZoom);
  const zoomTier = getZoomTier(zoom);

  const dueInEnabled = cardContents.manualDueInTimeEnabled;

  const {
    selectedCardId,
    isSelectionVisible,
    selectCard: selectCardBase,
  } = useRoPanelParams();
  const { logPanelOpened } = useRoPanelAnalytics();
  const selectCard = useCallback(
    (id: string) => {
      selectCardBase(id);
      logPanelOpened();
    },
    [selectCardBase, logPanelOpened]
  );
  // The selection survives closing the panel, but the ring should not: it only
  // means something while RO Details is actually on screen.
  const isSelected =
    isSelectionVisible && !!cardData && cardData.id === selectedCardId;

  // While the card menu is open, freeze the wiggle animation so the open
  // dropdown doesn't get dragged around by its moving trigger.
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      data-zoom-tier={zoomTier}
      className={cn(
        // Transition is scoped to named properties so card-size zoom applies
        // instantly (no size tween), while the hover shadow, colour changes
        // and the drag lift still animate.
        "card-container card-animated flex min-h-12 w-[150px] flex-1 cursor-auto flex-col items-start rounded-sm border border-solid border-[#0000000D] text-sm transition-[box-shadow,background-color,background-image,color,transform,opacity]",
        // Hover shadow only when the card is not selected, so it doesn't override the selection ring.
        !isSelected && "hover:[box-shadow:#00000026_0px_2px_6px]",
        isDragging && "-translate-y-12 rotate-6 scale-90 border opacity-75",
        tentative && "scale-90 border opacity-75 shadow-2xl",
        automationAction("wiggle") && !menuOpen && "animate-wiggle",
        // Selected: layered 1px black + 4px blue + 5px white ring (no blur),
        // shown instantly (box-shadow excluded from the transition list below),
        // while colour/transform/opacity still animate, and kept visible on hover.
        // This ring paints 5px OUTSIDE the card box (7.5px at the max zoom of 1.5),
        // so the container's scroll region pads for it — see the p-2 in
        // SortableContainer.tsx. Widening the ring here means widening that padding.
        isSelected &&
          "relative z-20 shadow-[0_0_0_1px_#000000,0_0_0_4px_#4682FF,0_0_0_5px_#FFFFFF] transition-[background-color,background-image,color,transform,opacity]"
      )}
      style={{
        willChange: isDragging ? "transform, opacity" : "auto",
        contain: "layout style paint",
        backgroundColor: getBackgroundColor(),
        backgroundImage: gradientFromColor(getBackgroundColor()),
        color: getTextColor(getBackgroundColor()),
        zoom,
      }}
      onClick={(e) => {
        // The card's action dialogs (Tags, Mark Complete, …) are React
        // descendants of this div but render into a portal at document.body.
        // React bubbles their clicks here anyway, which would open the RO panel
        // on a Save/Confirm press. Only clicks on the card itself count.
        if (isEventFromPortal(e)) return;
        if (cardData) {
          selectCard(cardData.id);
        }
      }}
    >
      {cardData && (
        <button
          type="button"
          className="sr-only"
          aria-pressed={isSelected}
          aria-label={`Select RO ${cardData.id}`}
          onClick={(e) => {
            e.stopPropagation();
            if (cardData) {
              selectCard(cardData.id);
            }
          }}
        />
      )}
      <div
        className={cn(
          "card-animated flex w-full flex-grow flex-col rounded-sm pt-1",
          automationAction("flash") && `animate-${animationName}`
        )}
        style={
          {
            "--flash-base-color": getBackgroundColor(),
          } as CSSProperties
        }
      >
        {cardData && (
          <Fragment>
            <div className="relative flex w-full flex-grow flex-row">
              <div className="flex w-full flex-grow-0 flex-col gap-1 overflow-hidden">
                <div className="">
                  <CardHeader
                    cardData={cardData}
                    shop={shop}
                    subdomain={shop.integrationSubdomain}
                    integration={integration}
                  />
                </div>
                <div
                  className="min-h-4 w-full flex-grow cursor-grab"
                  {...dragHandleAttributes}
                  {...dragHandleListeners}
                >
                  <CardMain
                    shop={shop}
                    cardData={cardData}
                    disabled={disabled}
                  />
                </div>
              </div>
              {!isDragging && !tentative && (
                <CardSidebar
                  cardData={cardData}
                  employees={employees}
                  shop={shop}
                  disabled={disabled}
                  onMoveToClick={onMoveToClick}
                  onMenuOpenChange={setMenuOpen}
                />
              )}
            </div>
            {!isDragging && !tentative && (
              <div className="flex w-full flex-col justify-end">
                {dueInEnabled && cardData.appointment?.startAt ? (
                  <CardRow size={"sm"}>
                    <DueInOutTime
                      time={cardData.appointment?.startAt}
                      type={"in"}
                    />
                  </CardRow>
                ) : null}
                {cardData.promisedTime ? (
                  <CardRow size={"sm"}>
                    <DueInOutTime
                      time={cardData.promisedTime}
                      type={"promise"}
                    />
                  </CardRow>
                ) : null}
                <CardRow>
                  <CardTags cardData={cardData} tags={tags?.tags} shop={shop} />
                </CardRow>
              </div>
            )}
            <div className="w-full rounded-b-sm">
              {!tentative && !isDragging && (
                <CardFooter
                  disabled={disabled}
                  cardData={cardData}
                  shopId={shop.id}
                  isCardColorEnabled={features.isCardColorEnabled}
                  isCardNotesEnabled={features.isCardNotesEnabled}
                  isCardAttachmentsEnabled={features.isAttachmentsEnabled}
                  isCardLaborEnabled={features.isCardLaborEnabled}
                  isTasksTabEnabled={features.isTasksTabEnabled}
                  isManualJobHoursEnabled={cardContents.manualJobHoursEnabled}
                  isManualCardOnly={features.isManualCardOnlyEnabled}
                  bgColor={getBackgroundColor()}
                  color={
                    cardData.color
                      ? cardData.color.includes("#")
                        ? (cardData.color as HexColor)
                        : `#${cardData.color}`
                      : DEFAULT_CARD_COLOR
                  }
                  timerUnits={shop.cardConfig.timerUnits}
                  defaultTimerValue={shop.defaultTimer}
                  {...props}
                />
              )}
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
};
